import type { TargetareCampanie, TargetareTinta } from "@/types/targetare";
import {
  raportImplicit,
  type DefinitieRaport,
  type FiltruRapidDef,
} from "@/utils/targetareRaport";
import {
  ETICHETE_DIMENSIUNE,
  ETICHETE_INCADRARE,
  ETICHETE_NIVEL_RISC,
  ETICHETE_TIER,
  type ColoanaTabel,
} from "@/utils/targetareTabel";
import {
  ETICHETE_PRIORITATE,
  ETICHETE_STARE_TINTA,
  STARI_TINTA,
  numeCampanie,
} from "@/utils/targetareTinte";

export type RandTinta = TargetareTinta & {
  campanie_nume: string;
  prioritate_text: string;
  cheie: string;
};

export function randuriTinte(
  tinte: TargetareTinta[],
  campanii: TargetareCampanie[]
): RandTinta[] {
  return tinte.map((t) => ({
    ...t,
    campanie_nume: numeCampanie(campanii, t.campanie_id) || "(fără campanie)",
    prioritate_text: ETICHETE_PRIORITATE[t.prioritate] ?? String(t.prioritate),
    cheie: String(t.id),
  }));
}

export const COLOANE_TINTE: ColoanaTabel<RandTinta>[] = [
  {
    id: "denumire",
    eticheta: "Firmă",
    tip: "text",
    latime: 240,
    implicit: true,
  },
  { id: "cui", eticheta: "CUI", tip: "text", latime: 100, implicit: true },
  {
    id: "judet",
    eticheta: "Județ",
    tip: "lista",
    latime: 110,
    implicit: false,
  },
  {
    id: "sector",
    eticheta: "Sector",
    tip: "lista",
    latime: 150,
    implicit: true,
  },
  {
    id: "stare",
    eticheta: "Stare",
    tip: "lista",
    latime: 150,
    implicit: true,
    optiuni: STARI_TINTA,
    etichete: ETICHETE_STARE_TINTA,
  },
  {
    id: "prioritate_text",
    eticheta: "Prioritate",
    tip: "lista",
    latime: 120,
    implicit: true,
    optiuni: ["Ridicată", "Normală", "Scăzută"],
  },
  {
    id: "responsabil",
    eticheta: "Responsabil",
    tip: "text",
    latime: 130,
    implicit: true,
  },
  {
    id: "urmatoarea_actiune_la",
    eticheta: "Următoarea acțiune",
    tip: "text",
    latime: 150,
    implicit: true,
  },
  {
    id: "campanie_nume",
    eticheta: "Campanie",
    tip: "lista",
    latime: 170,
    implicit: true,
  },
  { id: "note", eticheta: "Notă", tip: "text", latime: 200, implicit: true },
  {
    id: "email_principal",
    eticheta: "Email",
    tip: "text",
    latime: 220,
    implicit: true,
  },
  {
    id: "telefon_principal",
    eticheta: "Telefon",
    tip: "text",
    latime: 140,
    implicit: false,
  },
  { id: "site", eticheta: "Site", tip: "text", latime: 160, implicit: false },
  {
    id: "dimensiune_stricta",
    eticheta: "Dimensiune",
    tip: "lista",
    latime: 100,
    implicit: false,
    optiuni: ["mare", "mijlocie", "mica", "micro"],
    etichete: ETICHETE_DIMENSIUNE,
  },
  {
    id: "tier",
    eticheta: "Tier",
    tip: "lista",
    latime: 70,
    implicit: true,
    optiuni: ["A", "B", "C", "D"],
    etichete: ETICHETE_TIER,
  },
  {
    id: "clasificare_estimata",
    eticheta: "Încadrare",
    tip: "lista",
    latime: 120,
    implicit: true,
    optiuni: ["ESSENTIAL", "IMPORTANT", "OUT_OF_SCOPE"],
    etichete: ETICHETE_INCADRARE,
  },
  {
    id: "nivel_risc",
    eticheta: "Nivel de risc",
    tip: "lista",
    latime: 120,
    implicit: true,
    optiuni: ["esential", "important", "de_baza"],
    etichete: ETICHETE_NIVEL_RISC,
  },
  {
    id: "sectoare",
    eticheta: "Sectoare (univers)",
    tip: "multi",
    latime: 180,
    implicit: false,
  },
  {
    id: "salariati",
    eticheta: "Salariați",
    tip: "numar",
    latime: 90,
    implicit: false,
  },
  {
    id: "conducere",
    eticheta: "Conducere",
    tip: "text",
    latime: 240,
    implicit: false,
  },
  {
    id: "director_it",
    eticheta: "Responsabil IT",
    tip: "text",
    latime: 160,
    implicit: false,
  },
  {
    id: "email_persoana",
    eticheta: "Email persoană",
    tip: "text",
    latime: 200,
    implicit: false,
  },
  {
    id: "sursa",
    eticheta: "Sursa țintei",
    tip: "lista",
    latime: 110,
    implicit: false,
    optiuni: ["manual", "raport", "activitate"],
  },
  {
    id: "creat_la",
    eticheta: "Adăugată la",
    tip: "text",
    latime: 140,
    implicit: false,
  },
  {
    id: "actualizat_la",
    eticheta: "Actualizată la",
    tip: "text",
    latime: 140,
    implicit: false,
  },
];

export const FILTRE_RAPIDE_TINTE: FiltruRapidDef[] = [
  {
    coloana: "stare",
    eticheta: "Stare",
    optiuni: STARI_TINTA,
    etichete: ETICHETE_STARE_TINTA,
  },
  { coloana: "campanie_nume", eticheta: "Campanie" },
  { coloana: "sector", eticheta: "Sector" },
  {
    coloana: "prioritate_text",
    eticheta: "Prioritate",
    optiuni: ["Ridicată", "Normală", "Scăzută"],
  },
  {
    coloana: "nivel_risc",
    eticheta: "Nivel de risc",
    optiuni: ["esential", "important", "de_baza"],
    etichete: ETICHETE_NIVEL_RISC,
  },
  {
    coloana: "clasificare_estimata",
    eticheta: "Încadrare",
    optiuni: ["ESSENTIAL", "IMPORTANT", "OUT_OF_SCOPE"],
    etichete: ETICHETE_INCADRARE,
  },
  { coloana: "responsabil", eticheta: "Responsabil" },
];

const IMPLICITE = COLOANE_TINTE.filter((c) => c.implicit).map((c) => c.id);

export const definitieRaportTinte: DefinitieRaport<RandTinta> = {
  cheie: "targetare.rapoarte.tinte",
  coloane: COLOANE_TINTE,
  rapoarteImplicite: [
    raportImplicit("Toate țintele", IMPLICITE, {
      sortare: [{ id: "actualizat_la", desc: true }],
    }),
    raportImplicit("De contactat", IMPLICITE, {
      filtre: [
        {
          id: "f-stare",
          coloana: "stare",
          operator: "egal",
          valoare: "de_contactat",
          activ: true,
        },
      ],
      sortare: [{ id: "prioritate_text", desc: false }],
    }),
    raportImplicit("În lucru", IMPLICITE, {
      filtre: [
        {
          id: "f-lucru",
          coloana: "stare",
          operator: "in",
          valori: ["contactat", "oferta_trimisa", "in_discutie"],
          activ: true,
        },
      ],
      sortare: [{ id: "urmatoarea_actiune_la", desc: false }],
    }),
    raportImplicit("Cu email, fără campanie", IMPLICITE, {
      filtre: [
        {
          id: "f-email",
          coloana: "email_principal",
          operator: "nu_este_gol",
          activ: true,
        },
        {
          id: "f-camp",
          coloana: "campanie_nume",
          operator: "egal",
          valoare: "(fără campanie)",
          activ: true,
        },
      ],
    }),
  ],
  cheieRand: (r) => r.cheie,
  filtreRapide: FILTRE_RAPIDE_TINTE,
  numeFisier: "tinte",
  mesajGol:
    "Nicio firmă în evidență pentru filtrele alese. Adaugă firme din raportul de firme (selecție → Actions → Adaugă la ținte) sau lipind CUI-uri.",
};
